/**
 * AdoraCare - the treatment-course STAGE LADDER derivations
 * (DOCTOR-FLOW-AFTER-INTAKE-V1 D3; founder ruling 2026-08-15 + mockup MATCH).
 *
 * PURE derivations over the page-lifted journey read -- ZERO new wire, ZERO
 * schema, ZERO second source of truth (the ruled fence): a STAGE is an
 * ACCEPTED plan item of the course in served order (the server serves items
 * priority-ordered per visit; visits order by appointment time); a stage is
 * DONE when its per-visit completion evidence exists (20260717_1 -- the
 * decision axis is untouched: a completed item stays status 'accepted').
 *
 * HONESTY FENCES:
 *  - completionAvailability !== 'available' -> NO ladder (pre-apply the done
 *    flags are unknowable; a fabricated "Stage 1 of N" is forbidden).
 *  - zero accepted items -> NO ladder (nothing is a stage until accepted).
 *  - the returning consult's continue-header renders ONLY when a ladder
 *    exists, so a single-visit consult is byte-identical to today.
 */

import type { CourseJourneyResponse } from '@/dashboard/api';
import { CONSULT_NEXT_CLOSE_LABEL } from '@/dashboard/doctor/consult-next-band';

export interface CourseStageStep {
  readonly itemId: number;
  readonly name: string;
  readonly done: boolean;
}

export interface CourseStageLadder {
  readonly courseName: string | null;
  /** 1-based current stage: completed + 1, capped at the total (a fully
   *  completed course reads "Stage M of M"). */
  readonly stageNumber: number;
  readonly stageTotal: number;
  readonly steps: readonly CourseStageStep[];
  /** The first not-done accepted step (the D1 suggested reason), or null
   *  when the course is complete. */
  readonly nextStage: CourseStageStep | null;
  readonly complete: boolean;
}

/** Visits in journey order: appointment time ascending, unknown times last,
 *  case-sheet id as the total tiebreak (a comparator must be total). */
function orderedVisits(
  journey: CourseJourneyResponse,
): CourseJourneyResponse['visits'] {
  return [...journey.visits].sort((a, b) => {
    const ta = a.appointmentTimeIso ?? '';
    const tb = b.appointmentTimeIso ?? '';
    if (ta !== '' && tb !== '') {
      if (ta < tb) return -1;
      if (ta > tb) return 1;
    } else if (ta !== '') {
      return -1;
    } else if (tb !== '') {
      return 1;
    }
    return a.caseSheetId - b.caseSheetId;
  });
}

/** Derive the stage ladder, or null when no honest ladder exists. */
export function deriveCourseStageLadder(
  journey: CourseJourneyResponse | null,
): CourseStageLadder | null {
  if (journey === null) return null;
  if (journey.completionAvailability !== 'available') return null;
  const steps: CourseStageStep[] = [];
  for (const visit of orderedVisits(journey)) {
    for (const item of visit.plan.planItems ?? []) {
      if (item.status !== 'accepted') continue;
      steps.push({
        itemId: item.id,
        name: item.procedureName,
        done: (item.completedAt ?? null) !== null,
      });
    }
  }
  if (steps.length === 0) return null;
  const doneCount = steps.filter((s) => s.done).length;
  const nextStage = steps.find((s) => !s.done) ?? null;
  return {
    courseName: journey.courseName,
    stageNumber: Math.min(doneCount + 1, steps.length),
    stageTotal: steps.length,
    steps,
    nextStage,
    complete: doneCount === steps.length,
  };
}

/** The continue-header title: "Continue treatment - Stage 2 of 4". */
export function continueHeaderTitle(ladder: CourseStageLadder): string {
  return `Continue treatment - Stage ${ladder.stageNumber} of ${ladder.stageTotal}`;
}

/** The course line under the band title: the COURSE NAME.
 *
 *  It used to read "Root canal course - Stage 2 of 4", restating the counter
 *  the title gives on the line immediately above. The line's job is to say
 *  WHICH COURSE this stage belongs to; the title already says which stage.
 *  With no course name there is nothing else to say, so the stage fact
 *  remains the fallback rather than rendering an empty line. */
export function stageCourseLine(ladder: CourseStageLadder): string {
  const stage = `Stage ${ladder.stageNumber} of ${ladder.stageTotal}`;
  return ladder.courseName !== null && ladder.courseName.trim() !== ''
    ? ladder.courseName
    : stage;
}

/** The NEXT-VISIT panel title: the first not-done stage, in ladder numbering
 *  ("Stage 3 of 4: Crown placement"); null when the course is complete (the
 *  panel then renders its plain follow-up voice). */
export function nextVisitStageTitle(ladder: CourseStageLadder): string | null {
  if (ladder.nextStage === null) return null;
  const idx = ladder.steps.findIndex((s) => s.itemId === ladder.nextStage?.itemId);
  return `Stage ${idx + 1} of ${ladder.stageTotal}: ${ladder.nextStage.name}`;
}

/**
 * D3 dock override: on a course visit whose ladder still has a not-done
 * stage, the CLOSE rung -- and ONLY the close rung; every earlier W-23 /
 * one-flow rung (start / intake / decisions) stands byte-identical -- speaks
 * the stage instead ("one obvious next action per stage", founder ruling
 * 2026-08-15). A finalized visit and a complete course keep the band's own
 * label.
 */
export function consultStageDockLabel(
  bandLabel: string,
  ladder: CourseStageLadder | null,
  sheet: { readonly status: string } | null,
): string {
  if (ladder === null || ladder.nextStage === null) return bandLabel;
  if (sheet !== null && sheet.status === 'finalized') return bandLabel;
  if (bandLabel !== CONSULT_NEXT_CLOSE_LABEL) return bandLabel;
  // THE STAGE COUNTER IS STATED ONCE, IN THE BAND THAT OWNS IT.
  // This label used to read "Continue treatment - Stage N of M: <name>",
  // repeating verbatim the counter the continue-treatment band states directly
  // below it -- and the course card's progress bar encodes the same progress a
  // third time. W-23 asks this dock to describe the next EXECUTABLE ACTION;
  // the actionable part is WHICH STAGE, not the arithmetic. The counter keeps
  // its ruled home (D3's band); the dock keeps the action and the stage name.
  return `Continue treatment: ${ladder.nextStage.name}`;
}

/**
 * D1 same-as-last-time suggestion: the most recent OTHER visit's recorded
 * follow-up token (an HONEST derivation from what the course actually did --
 * never an invented default). null = no prior recommendation to suggest.
 */
export function suggestedFollowUpOption(
  journey: CourseJourneyResponse | null,
  currentCaseSheetId: number | null,
): string | null {
  if (journey === null) return null;
  const prior = orderedVisits(journey)
    .filter(
      (v) =>
        v.caseSheetId !== currentCaseSheetId &&
        (v.plan.followUpOption ?? null) !== null,
    )
    .pop();
  return prior?.plan.followUpOption ?? null;
}

// --- PATIENT-360-V1 (founder ruling D-360d, 2026-08-16): the COUNTS-grain
// --- ladder for the ONE patient page (both role variants) -------------------
//
// The SAME D3 semantics at the counts grain the patient-page wires already
// carry (treatmentJourney courses: acceptedItemCount / completedItemCount) --
// zero wire change, zero second derivation home (this file stays the ONE
// stage-ladder owner). Named stages stay the consult's (the ruled v1 fence).

export interface CountsStageLadder {
  /** 1-based current stage: completed + 1, capped at the total. */
  readonly stageNumber: number;
  readonly stageTotal: number;
  readonly doneCount: number;
  readonly complete: boolean;
}

/**
 * Derive the counts ladder, or null when no honest ladder exists. HONESTY
 * FENCES (the D3 rules at counts grain): a null/absent completed count
 * (pre-apply completion truth unavailable) or zero accepted items -> NO
 * ladder -- a fabricated "Stage 1 of N" is forbidden.
 */
export function deriveCountsStageLadder(
  acceptedItemCount: number | null | undefined,
  completedItemCount: number | null | undefined,
): CountsStageLadder | null {
  if (
    typeof acceptedItemCount !== 'number' ||
    !Number.isFinite(acceptedItemCount) ||
    acceptedItemCount <= 0
  ) {
    return null;
  }
  if (
    typeof completedItemCount !== 'number' ||
    !Number.isFinite(completedItemCount) ||
    completedItemCount < 0
  ) {
    return null;
  }
  const doneCount = Math.min(completedItemCount, acceptedItemCount);
  return {
    stageNumber: Math.min(doneCount + 1, acceptedItemCount),
    stageTotal: acceptedItemCount,
    doneCount,
    complete: doneCount === acceptedItemCount,
  };
}

/** The counts-ladder line: "Stage 2 of 3" ("Course complete - 3 of 3 done"
 *  when every stage is done -- honest completion, never a fake next). */
export function countsStageLine(ladder: CountsStageLadder): string {
  return ladder.complete
    ? `Course complete - ${ladder.doneCount} of ${ladder.stageTotal} done`
    : `Stage ${ladder.stageNumber} of ${ladder.stageTotal}`;
}
